<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="`Kelola Dokumen - ${eventTitle}`"
    width="700px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <!-- Upload Section -->
    <div v-if="canEdit" class="upload-section">
      <el-alert
        title="Upload Dokumen"
        type="info"
        :closable="false"
        style="margin-bottom: 15px;"
      >
        <template slot="default">
          Upload dokumen terkait event ini (max 3MB per file)
        </template>
      </el-alert>

      <el-upload
        ref="upload"
        :auto-upload="false"
        :on-change="handleFileChange"
        :on-remove="handleFileRemove"
        :file-list="fileList"
        :limit="1"
        action="#"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
      >
        <el-button slot="trigger" size="small" type="primary" icon="el-icon-upload">
          Pilih File
        </el-button>
        <el-button
          style="margin-left: 10px;"
          size="small"
          type="success"
          :loading="uploading"
          :disabled="fileList.length === 0"
          @click="submitUpload"
        >
          Upload
        </el-button>
        <div slot="tip" class="el-upload__tip">
          File PDF, Word, Excel, PowerPoint, atau gambar (max 3MB)
        </div>
      </el-upload>
    </div>

    <el-divider v-if="canEdit"></el-divider>

    <!-- Attachments List -->
    <div class="attachments-list">
      <div v-if="loading" style="text-align: center; padding: 20px;">
        <i class="el-icon-loading"></i> Memuat dokumen...
      </div>

      <el-empty
        v-else-if="attachments.length === 0"
        description="Belum ada dokumen"
        :image-size="100"
      ></el-empty>

      <el-table
        v-else
        :data="attachments"
        border
        style="width: 100%"
      >
        <el-table-column
          type="index"
          label="No"
          width="50"
          align="center"
        ></el-table-column>

        <el-table-column label="Nama File" min-width="200">
          <template slot-scope="scope">
            <i :class="getFileIcon(scope.row.name)"></i>
            {{ scope.row.name }}
          </template>
        </el-table-column>

        <el-table-column label="Ukuran" width="100" align="center">
          <template slot-scope="scope">
            {{ formatFileSize(scope.row.size) }}
          </template>
        </el-table-column>

        <el-table-column label="Tanggal Upload" width="150" align="center">
          <template slot-scope="scope">
            {{ formatDate(scope.row.lastModifiedDateTime) }}
          </template>
        </el-table-column>

        <el-table-column label="Aksi" width="180" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="primary"
              size="mini"
              icon="el-icon-download"
              @click="handleDownload(scope.row)"
            >
              Download
            </el-button>
            <el-button
              type="danger"
              size="mini"
              icon="el-icon-delete"
              :disabled="!canEdit"
              @click="handleDelete(scope.row)"
            >
              Hapus
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Tutup</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'EventAttachmentsDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    event: {
      type: Object,
      default: () => ({})
    },
    currentUserEmail: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      dialogVisible: false,
      attachments: [],
      fileList: [],
      uploading: false,
      loading: false,
      selectedFile: null
    };
  },
  computed: {
    eventTitle() {
      return this.event?.subject || 'Event';
    },
    canEdit() {
      // Only organizer can upload/delete attachments
      return this.event?.organizerEmail === this.currentUserEmail;
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
      if (val) {
        this.loadAttachments();
      }
    },
    dialogVisible(val) {
      if (!val) {
        this.$emit('update:visible', false);
      }
    }
  },
  methods: {
    ...mapActions('integration', [
      'getEventAttachments',
      'uploadEventAttachment',
      'deleteEventAttachment',
      'downloadEventAttachment'
    ]),

    async loadAttachments() {
      if (!this.event?.id || !this.event?.organizerEmail) {
        return;
      }

      this.loading = true;
      try {
        this.attachments = await this.getEventAttachments({
          userIdOrUpn: this.event.organizerEmail,
          eventId: this.event.id
        });
      } catch (error) {
        console.error('Error loading attachments:', error);
        this.$message.error('Gagal memuat dokumen: ' + error.message);
      } finally {
        this.loading = false;
      }
    },

    handleFileChange(file, fileList) {
      // Check file size (3MB limit)
      const isLt3M = file.size / 1024 / 1024 < 3;
      if (!isLt3M) {
        this.$message.error('Ukuran file tidak boleh lebih dari 3MB!');
        fileList.pop(); // Remove the file
        return;
      }
      this.fileList = fileList;
      this.selectedFile = file.raw;
    },

    handleFileRemove() {
      this.fileList = [];
      this.selectedFile = null;
    },

    async submitUpload() {
      if (!this.selectedFile) {
        this.$message.warning('Silakan pilih file terlebih dahulu');
        return;
      }

      if (!this.canEdit) {
        this.$message.error('Hanya organizer yang dapat upload dokumen');
        return;
      }

      this.uploading = true;
      try {
        await this.uploadEventAttachment({
          userIdOrUpn: this.event.organizerEmail,
          eventId: this.event.id,
          file: this.selectedFile,
          fileName: this.selectedFile.name
        });

        this.$message.success('Dokumen berhasil diupload!');
        
        // Reset upload form
        this.fileList = [];
        this.selectedFile = null;
        this.$refs.upload.clearFiles();

        // Reload attachments
        await this.loadAttachments();
      } catch (error) {
        console.error('Error uploading attachment:', error);
        this.$message.error('Gagal upload dokumen: ' + (error.message || 'Unknown error'));
      } finally {
        this.uploading = false;
      }
    },

    async handleDownload(attachment) {
      try {
        await this.downloadEventAttachment({
          userIdOrUpn: this.event.organizerEmail,
          eventId: this.event.id,
          attachmentId: attachment.id,
          fileName: attachment.name
        });

        this.$message.success('Dokumen berhasil didownload!');
      } catch (error) {
        console.error('Error downloading attachment:', error);
        this.$message.error('Gagal mendownload dokumen: ' + (error.message || 'Unknown error'));
      }
    },

    handleDelete(attachment) {
      this.$confirm(
        `Apakah Anda yakin ingin menghapus dokumen "${attachment.name}"?`,
        'Konfirmasi Hapus',
        {
          confirmButtonText: 'Ya, Hapus',
          cancelButtonText: 'Batal',
          type: 'warning'
        }
      ).then(async () => {
        try {
          await this.deleteEventAttachment({
            userIdOrUpn: this.event.organizerEmail,
            eventId: this.event.id,
            attachmentId: attachment.id
          });

          this.$message.success('Dokumen berhasil dihapus!');
          await this.loadAttachments();
        } catch (error) {
          console.error('Error deleting attachment:', error);
          this.$message.error('Gagal menghapus dokumen: ' + (error.message || 'Unknown error'));
        }
      }).catch(() => {
        // User cancelled
      });
    },

    handleClose() {
      this.dialogVisible = false;
      this.fileList = [];
      this.selectedFile = null;
    },

    getFileIcon(fileName) {
      const extension = fileName.split('.').pop().toLowerCase();
      const iconMap = {
        pdf: 'el-icon-document',
        doc: 'el-icon-document',
        docx: 'el-icon-document',
        xls: 'el-icon-tickets',
        xlsx: 'el-icon-tickets',
        ppt: 'el-icon-data-line',
        pptx: 'el-icon-data-line',
        jpg: 'el-icon-picture',
        jpeg: 'el-icon-picture',
        png: 'el-icon-picture'
      };
      return iconMap[extension] || 'el-icon-document';
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      
      try {
        const date = new Date(dateString);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return '-';
        }
        
        return date.toLocaleDateString('id-ID', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      } catch (error) {
        console.error('Error formatting date:', dateString, error);
        return '-';
      }
    }
  }
};
</script>

<style scoped>
.upload-section {
  margin-bottom: 20px;
}

.attachments-list {
  margin-top: 20px;
}

.el-icon-document,
.el-icon-tickets,
.el-icon-data-line,
.el-icon-picture {
  margin-right: 5px;
  color: #409EFF;
}
</style>
